// src/hooks/useLabPatients.ts

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  LAB_STORAGE_KEYS,
  loadLabStorage,
  removeLabStorage,
  saveLabStorage,
} from "../utils/labStorage";

import {
  LAB_PATIENTS,
} from "../data/labMockData";

import type {
  CompletedTest,
  OngoingTest,
  Patient,
  TestHistory,
} from "../types/lab-context.types";

interface UseLabPatientsOptions {
  ongoingTests: OngoingTest[];
  completedTests: CompletedTest[];
}

interface UseLabPatientsReturn {
  patients: Patient[];

  getPatientTestHistory: (
    patientId: string,
  ) => TestHistory[];

  clearPatients: () => void;
  resetPatients: () => void;
}

/**
 * Manages laboratory patient records and patient test history.
 *
 * Test history is derived from the current ongoing and completed
 * test collections rather than being stored separately.
 */
export const useLabPatients = ({
  ongoingTests,
  completedTests,
}: UseLabPatientsOptions): UseLabPatientsReturn => {
  const [patients, setPatients] =
    useState<Patient[]>(() =>
      loadLabStorage(
        LAB_STORAGE_KEYS.PATIENTS,
        LAB_PATIENTS,
      ),
    );

  useEffect(() => {
    saveLabStorage(
      LAB_STORAGE_KEYS.PATIENTS,
      patients,
    );
  }, [patients]);

  /**
   * Returns a patient's completed and active laboratory history,
   * sorted with the newest entry first.
   */
  const getPatientTestHistory = useCallback(
    (
      patientId: string,
    ): TestHistory[] => {
      const completedHistory:
        TestHistory[] =
        completedTests
          .filter(
            (test) =>
              test.patientId === patientId,
          )
          .map((test) => ({
            id: test.id,
            testType: test.testType,
            date: test.completedDate,
            requestedBy:
              test.requestedBy,
            status: "Completed",
            result:
              test.findings
                .split("\n")[0]
                ?.trim() ||
              "Result available",
          }));

      const ongoingHistory:
        TestHistory[] =
        ongoingTests
          .filter(
            (test) =>
              test.patientId === patientId,
          )
          .map((test) => ({
            id: test.id,
            testType: test.testType,
            date:
              test.startTime.includes("T")
                ? test.startTime.split("T")[0]
                : test.startTime.split(" ")[0],
            requestedBy:
              test.requestedBy,
            status: "In progress",
            result: "—",
          }));

      return [
        ...completedHistory,
        ...ongoingHistory,
      ].sort(
        (
          firstRecord,
          secondRecord,
        ) => {
          const firstDate =
            new Date(
              firstRecord.date,
            ).getTime();

          const secondDate =
            new Date(
              secondRecord.date,
            ).getTime();

          const safeFirstDate =
            Number.isNaN(firstDate)
              ? 0
              : firstDate;

          const safeSecondDate =
            Number.isNaN(secondDate)
              ? 0
              : secondDate;

          return (
            safeSecondDate -
            safeFirstDate
          );
        },
      );
    },
    [
      completedTests,
      ongoingTests,
    ],
  );

  const clearPatients =
    useCallback((): void => {
      removeLabStorage(
        LAB_STORAGE_KEYS.PATIENTS,
      );

      setPatients([]);
    }, []);

  const resetPatients =
    useCallback((): void => {
      setPatients(
        LAB_PATIENTS,
      );
    }, []);

  return {
    patients,

    getPatientTestHistory,

    clearPatients,
    resetPatients,
  };
};
